import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import type { HmsApprovalMode, RiskMatrixOverview, RiskMatrixSettings } from '../../../api/hms';
import {
  useApproveHmsRiskMatrixMutation,
  useHmsRiskMatrixQuery,
  useSubmitHmsRiskMatrixMutation,
  useWithdrawHmsRiskMatrixMutation,
} from '../../../hooks/queries/hms/useHmsQueries';
import { useUsesSafetyReps } from '../../../hooks/queries/hms/useHmsSettingsQuery';
import { safetyRepText } from '../safetyRepText';
import { diffSettings, sendErrors } from './riskMatrixView';
import { initialApprovalMode } from '../hmsApproval';
import { LevelsCard } from './components/LevelsCard';
import { LikelihoodCard } from './components/LikelihoodCard';
import { ConsequenceCard } from './components/ConsequenceCard';
import { RulesCard } from './components/RulesCard';
import { SendCard } from './components/SendCard';
import { SidePanel } from './components/SidePanel';
import { GettingStartedGuide } from './components/GettingStartedGuide';
import layout from '../hmsPage.module.css';

export const SEND_SECTION_ID = 'hr-risk-matrix-send';

export const HRRiskMatrix = (): ReactElement => {
  const { t } = useTranslation();
  const text = safetyRepText(useUsesSafetyReps());
  const { data, isLoading, error } = useHmsRiskMatrixQuery();

  const content = () => {
    if (isLoading) {
      return (
        <div aria-busy="true" className={layout.loading}>
          <span className="ds-sr-only">{t('hrRiskMatrix.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={420} />
          <Skeleton variant="rectangle" width="100%" height={260} />
        </div>
      );
    }
    if (error || !data) {
      return (
        <Alert data-color="danger">
          <Heading level={2} data-size="2xs">
            {t('hrRiskMatrix.error.title')}
          </Heading>
          <Paragraph>{t('hrRiskMatrix.error.message')}</Paragraph>
        </Alert>
      );
    }
    return <RiskMatrixForm overview={data} />;
  };

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          <span className={layout.eyebrow}>
            {data?.organizationName
              ? t('hms.eyebrowWithOrganization', { organization: data.organizationName })
              : t('hms.eyebrow')}
          </span>
          <Heading level={1} data-size="xl">
            {t('hrRiskMatrix.title')}
          </Heading>
          <Paragraph className={layout.intro}>{t(text('hrRiskMatrix.intro'))}</Paragraph>
        </div>
      </section>
      {content()}
    </div>
  );
};

const RiskMatrixForm = ({ overview }: { overview: RiskMatrixOverview }): ReactElement => {
  const { t } = useTranslation();
  const base = overview.current.settings;
  const { pending, returned, hasMainSafetyRep, mainSafetyRepIsYou } = overview;
  // The form shows what waits for approval, or what the safety representative sent back to be corrected
  const [settings, setSettings] = useState<RiskMatrixSettings>(pending?.settings ?? returned?.settings ?? base);
  const [changeReason, setChangeReason] = useState(returned?.changeReason ?? '');
  const [mode, setMode] = useState<HmsApprovalMode>(initialApprovalMode(hasMainSafetyRep));
  const [comment, setComment] = useState('');
  const [tried, setTried] = useState(false);
  const [justApproved, setJustApproved] = useState(false);

  const submit = useSubmitHmsRiskMatrixMutation();
  const approve = useApproveHmsRiskMatrixMutation();
  const withdraw = useWithdrawHmsRiskMatrixMutation();

  const changes = diffSettings(base, settings, overview.factors);
  const dirty = changes.length > 0;

  const errors = () => sendErrors(
      { settings, factors: overview.factors, changeReason, mode, comment, hasMainSafetyRep, mainSafetyRepIsYou },
      t,
    );
  const shownErrors = tried ? errors() : null;

  const update = (next: Partial<RiskMatrixSettings>) => {
    setSettings((current) => ({ ...current, ...next }));
    setJustApproved(false);
  };

  const resetForm = () => {
    setChangeReason('');
    setComment('');
    setTried(false);
  };

  const send = () => {
    if (errors().length > 0) {
      setTried(true);
      return;
    }
    submit.mutate(
      { settings, changeReason: changeReason.trim(), approval: { mode, comment: comment.trim() || undefined } },
      {
        onSuccess: (next) => {
          setSettings(next.pending?.settings ?? next.current.settings);
          resetForm();
          setJustApproved(next.pending === null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
      },
    );
  };

  const discard = () => {
    setSettings(base);
    resetForm();
    if (returned) withdraw.mutate(returned.id);
  };

  return (
    <div className={layout.layout}>
      <div className={layout.main}>
        <GettingStartedGuide />
        <LevelsCard settings={settings} options={overview.options} onChange={update} />
        <LikelihoodCard settings={settings} onChange={update} />
        <ConsequenceCard
          settings={settings}
          factors={overview.factors}
          onChange={(next) => {
            setSettings(next);
            setJustApproved(false);
          }}
        />
        <RulesCard
          settings={settings}
          factors={overview.factors}
          options={overview.options}
          onChange={update}
          showUrlErrors={tried}
        />
        {dirty && !pending && (
          <SendCard
            id={SEND_SECTION_ID}
            changes={changes}
            factors={overview.factors}
            currentVersion={overview.current.version}
            changeReason={changeReason}
            onChangeReason={setChangeReason}
            approval={{ hasMainSafetyRep, mainSafetyRepIsYou, mode, onModeChange: setMode, comment, onCommentChange: setComment }}
            errors={shownErrors}
            send={{ run: send, pending: submit.isPending }}
            onDiscard={discard}
            sendFailed={submit.isError}
          />
        )}
      </div>
      <SidePanel
        overview={overview}
        sendSectionId={SEND_SECTION_ID}
        unsentChanges={dirty && !pending ? changes.length : 0}
        justApproved={justApproved}
        approve={{
          run: (id, approvalComment) =>
            approve.mutate(
              { id, comment: approvalComment },
              {
                onSuccess: (next) => {
                  setSettings(next.current.settings);
                  setJustApproved(true);
                },
              },
            ),
          pending: approve.isPending,
        }}
        withdraw={{
          // The form goes back to the version in use, so nothing looks unsaved afterwards
          run: (id) =>
            withdraw.mutate(id, {
              onSuccess: (next) => {
                setSettings(next.pending?.settings ?? next.current.settings);
                resetForm();
                setJustApproved(false);
              },
            }),
          pending: withdraw.isPending,
        }}
        actionFailed={approve.isError || withdraw.isError}
      />
    </div>
  );
};
